import { db } from "../firebase-config"; // Adjust the import path as needed
import { doc, getDoc } from "firebase/firestore";

// Function to retrieve data from Firestore
async function getData(questionnaireId) {
    try {
        // 1. Fetch the questionnaire document
        const questionnaireDoc = await getDoc(doc(db, "Questionnaires", questionnaireId));
        if (!questionnaireDoc.exists()) {
            throw new Error("Questionnaire not found");
        }

        const questionnaireData = questionnaireDoc.data();
        const questions = questionnaireData.questions || [];
        const studentsPerTeam = questionnaireData.studentsPerTeam || 3;

        // 2. Create weightFun mapping
        const weightFun = {};
        questions.forEach((question, index) => {
            weightFun[`question${index}`] = question.WeightFun;
        });

        // 3. Fetch responses (now from single document)
        const responsesDoc = await getDoc(doc(db, "Responses", questionnaireId));
        let studentResponses = [];
        
        if (responsesDoc.exists()) {
            const responsesData = responsesDoc.data();
            // Transform the responses array into the expected format
            studentResponses = (responsesData.responses || []).map(response => ({
                uid: response.uid,
                responses: response.answers, // Changed from 'answers' to match your structure
                timestamp: response.timestamp
            }));
        }

        return { 
            questions, 
            studentResponses, 
            weightFun, 
            studentsPerTeam 
        };
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
}

// Function to sort students based on weightFun
function sortStudents(studentResponses, weightFun) {
    const students = studentResponses.map((response, index) => ({ id: index, ...response }));

    // Sort students based on weightFun
    for (const [questionKey, weight] of Object.entries(weightFun).sort((a, b) => (a[1] === "balance" ? 1 : -1))) {
        if (weight === "group") {
            // Group students by their response to this question
            students.sort((a, b) => a.responses[questionKey]?.localeCompare(b.responses[questionKey]));
        } else if (weight === "balance") {
            // Balance students by their response to this question
            students.sort((a, b) => a.responses[questionKey]?.localeCompare(b.responses[questionKey]));
        }
    }

    return students;
}

// Function to allocate students to teams
function allocateTeams(sortedStudents, numGroups) {
    const teams = Array.from({ length: numGroups }, () => []);

    // Distribute students evenly across teams
    sortedStudents.forEach((student, index) => {
        const teamIdx = index % numGroups;
        teams[teamIdx].push(student);
    });

    return teams;
}

// Main function to allocate teams
export async function runTeamAllocation(questionnaireId) {
    try {
        // Fetch data from Firestore
        const { questions, studentResponses, weightFun, studentsPerTeam } = await getData(questionnaireId);

        // Sort students based on weightFun
        const sortedStudents = sortStudents(studentResponses, weightFun);

        // Determine the number of teams based on studentsPerTeam
        const numGroups = Math.ceil(studentResponses.length / studentsPerTeam);

        // Allocate students to teams
        const teams = allocateTeams(sortedStudents, numGroups);

        return teams;
    } catch (error) {
        console.error("Error running team allocation:", error);
        throw error;
    }
}